import React, { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { apiRequest } from "services/tenantUserAdminService";
import { TC } from "../tokens/text";

const VAT_SCHEMES = [
  { value: "standard",  label: "Standard (Accrual)" },
  { value: "cash",      label: "Cash Accounting" },
  { value: "flat_rate", label: "Flat Rate" },
  { value: "retail",    label: "Retail" },
];

const STAGGER_MONTHS = [
  { value: 1,  label: "Jan / Apr / Jul / Oct" },
  { value: 2,  label: "Feb / May / Aug / Nov" },
  { value: 3,  label: "Mar / Jun / Sep / Dec (default)" },
];

const CARD_BG = "linear-gradient(127.09deg, rgba(6,11,40,0.94) 19.41%, rgba(10,14,35,0.49) 76.65%)";

const inputSx = {
  "& .MuiInputBase-input": { color: "#fff" },
  "& .MuiInputLabel-root": { color: TC.secondary },
  "& .MuiOutlinedInput-notchedOutline": { bordercolor: "#7a94b0" },
  "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { bordercolor: "#4a6080" },
  "& .MuiSelect-icon": { color: TC.secondary },
  mb: 2,
};

/*Bearer ${token}`
 * VAT Scheme Configurator
 * Loads and saves per-client VAT settings (VRN, scheme, stagger, etc.)
 *
 * Props:
 *   clientId  — Firestore clientProfile ID
 *   tenantId  — tenant ID from auth context
 *   onSaved   — optional callback after successful save
 */
const VATSchemeConfigurator = ({ clientId, tenantId, onSaved }) => {
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState("");
  const [success, setSuccess]     = useState(false);

  const [vrn, setVrn]                       = useState("");
  const [scheme, setScheme]                 = useState("standard");
  const [staggerMonth, setStaggerMonth]     = useState(3);
  const [effectiveDate, setEffectiveDate]   = useState("");
  const [flatRatePercent, setFlatRate]      = useState("");
  const [partialExemption, setPartialEx]    = useState(false);

  useEffect(() => {
    if (!clientId || !tenantId) return;
    setLoading(true);
    apiRequest(`/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/vat-settings`)
      .then(data => {
        if (data && Object.keys(data).length > 0) {
          setVrn(data.vrn || "");
          setScheme(data.scheme || "standard");
          setStaggerMonth(data.staggerMonth || 3);
          setEffectiveDate(data.effectiveDate || "");
          setFlatRate(data.flatRatePercent != null ? String(data.flatRatePercent) : "");
          setPartialEx(data.partialExemption || false);
        }
      })
      .catch(() => {}) // no settings yet — defaults are fine
      .finally(() => setLoading(false));
  }, [clientId, tenantId]);

  const handleSave = async (e) => {
    e.preventDefault();
    if (!clientId || !tenantId) return;
    setSaving(true); setError(""); setSuccess(false);
    try {
      await apiRequest(
        `/tenant/${encodeURIComponent(tenantId)}/clients/${encodeURIComponent(clientId)}/vat-settings`,
        {
          method: "POST",
          body: {
            vrn: vrn.trim() || undefined,
            scheme,
            staggerMonth: Number(staggerMonth),
            effectiveDate: effectiveDate || undefined,
            flatRatePercent: scheme === "flat_rate" && flatRatePercent ? Number(flatRatePercent) : undefined,
            partialExemption,
          },
        }
      );
      setSuccess(true);
      if (onSaved) onSaved();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <VuiBox display="flex" justifyContent="center" p={3}>
        <CircularProgress size={24} sx={{ color: "#4E72CE" }} />
      </VuiBox>
    );
  }

  return (
    <Card sx={{ background: CARD_BG, border: "1px solid #d8e6f7", borderRadius: "16px" }}>
      <VuiBox p={3}>
        <VuiTypography sx={{ color: TC.primary, fontSize: 15, fontWeight: 700, mb: 2 }}>
          ⚙️ VAT Configuration
        </VuiTypography>

        {error && (
          <VuiBox mb={2} sx={{ background: "rgba(255,71,87,0.1)", border: "1px solid rgba(255,71,87,0.3)", borderRadius: 2, p: "10px 14px" }}>
            <VuiTypography sx={{ color: "#f44336", fontSize: 13 }}>{error}</VuiTypography>
          </VuiBox>
        )}
        {success && (
          <VuiBox mb={2} sx={{ background: "rgba(1,181,116,0.1)", border: "1px solid rgba(1,181,116,0.3)", borderRadius: 2, p: "10px 14px" }}>
            <VuiTypography sx={{ color: "#01B574", fontSize: 13 }}>VAT settings saved ✓</VuiTypography>
          </VuiBox>
        )}

        <form onSubmit={handleSave}>
          <TextField
            label="VAT Registration Number (VRN)"
            placeholder="GB123456789"
            value={vrn}
            onChange={e => setVrn(e.target.value)}
            fullWidth
            sx={inputSx}
            inputProps={{ maxLength: 12 }}
          />

          <FormControl fullWidth sx={inputSx}>
            <InputLabel sx={{ color: TC.secondary }}>VAT Scheme</InputLabel>
            <Select
              value={scheme}
              onChange={e => setScheme(e.target.value)}
              label="VAT Scheme"
              sx={{ color: TC.primary, "& .MuiOutlinedInput-notchedOutline": { bordercolor: "#7a94b0" } }}
            >
              {VAT_SCHEMES.map(s => (
                <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          {scheme === "flat_rate" && (
            <TextField
              label="Flat Rate %"
              type="number"
              value={flatRatePercent}
              onChange={e => setFlatRate(e.target.value)}
              fullWidth
              sx={inputSx}
              inputProps={{ min: 0, max: 100, step: 0.1 }}
            />
          )}

          <FormControl fullWidth sx={inputSx}>
            <InputLabel sx={{ color: TC.secondary }}>VAT Quarter Stagger</InputLabel>
            <Select
              value={staggerMonth}
              onChange={e => setStaggerMonth(e.target.value)}
              label="VAT Quarter Stagger"
              sx={{ color: TC.primary, "& .MuiOutlinedInput-notchedOutline": { bordercolor: "#7a94b0" } }}
            >
              {STAGGER_MONTHS.map(m => (
                <MenuItem key={m.value} value={m.value}>{m.label}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField
            label="VAT Registration Effective Date"
            type="date"
            value={effectiveDate}
            onChange={e => setEffectiveDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
            sx={inputSx}
          />

          <FormControlLabel
            control={
              <Switch
                checked={partialExemption}
                onChange={e => setPartialEx(e.target.checked)}
                sx={{ "& .MuiSwitch-thumb": { background: "#4E72CE" } }}
              />
            }
            label={<VuiTypography sx={{ color: TC.midtone, fontSize: 13 }}>Partial Exemption Applies</VuiTypography>}
            sx={{ mb: 2 }}
          />

          <VuiButton
            type="submit"
            color="info"
            variant="contained"
            fullWidth
            disabled={saving}
            sx={{ fontWeight: 700 }}
          >
            {saving ? "Saving…" : "Save VAT Settings"}
          </VuiButton>
        </form>
      </VuiBox>
    </Card>
  );
};

export default VATSchemeConfigurator;



